import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { SEOHead } from '@/components/seo';
import { Header, Footer } from '@/components/public';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  Check, 
  ChevronRight,
  FileText
} from 'lucide-react';

const SITE_NAME = 'StartEase';
const BASE_URL = 'https://starteaseai.com';
const APP_URL = '/login';

const FILING_CONFIG = {
  'articles-of-organization': {
    name: 'Articles of Organization',
    shortName: 'Articles of Organization',
    entityType: 'LLC',
    description: 'The foundational document that officially creates your LLC with the state. This is the first and most important filing for any new LLC.',
    longDescription: `Articles of Organization (also called Certificate of Formation or Certificate of Organization in some states) is the legal document filed with the state to officially create your Limited Liability Company (LLC).

This document establishes your LLC as a legal entity, separate from you personally. Once filed and approved by the state, your LLC officially exists and can begin conducting business.`,
    whatIncluded: [
      'LLC name and principal address',
      'Registered agent name and address',
      'Purpose of the business (general or specific)',
      'Management structure (member-managed or manager-managed)',
      'Names of organizers',
      'Duration of the LLC (perpetual or specific term)',
      'Effective date of formation'
    ],
    whyNeeded: [
      'Legally creates your LLC as a separate entity',
      'Provides personal liability protection',
      'Required to open business bank accounts',
      'Necessary for obtaining business licenses',
      'Establishes credibility with customers and vendors'
    ],
    timeline: '3-10 business days',
    price: 199,
    relatedFilings: ['operating-agreement', 'ein-registration', 'registered-agent-appointment']
  },
  'operating-agreement': {
    name: 'Operating Agreement',
    shortName: 'Operating Agreement',
    entityType: 'LLC',
    description: 'The internal document that outlines how your LLC will be run, including ownership structure, profit distribution, and decision-making processes.',
    longDescription: `An Operating Agreement is an internal document that establishes the rules for how your LLC will operate. While not always required by law, it's essential for protecting your limited liability status and preventing disputes.

This document defines the rights and responsibilities of LLC members, how profits and losses are distributed, voting procedures, and what happens if a member leaves or the LLC dissolves.`,
    whatIncluded: [
      'Ownership percentages and capital contributions',
      'Profit and loss distribution',
      'Voting rights and decision-making procedures',
      'Management structure and responsibilities',
      'Meeting requirements and procedures',
      'Rules for adding or removing members',
      'Dissolution procedures',
      'Buy-sell provisions'
    ],
    whyNeeded: [
      'Protects your limited liability status',
      'Prevents disputes between members',
      'Required by banks to open business accounts',
      'Establishes clear ownership structure',
      'Provides flexibility in management and profit sharing'
    ],
    timeline: 'Same day (after LLC formation)',
    price: 0,
    includedWith: 'LLC Formation',
    relatedFilings: ['articles-of-organization', 'member-manager-change', 'name-amendment']
  },
  'annual-report': {
    name: 'Annual Report / Renewal',
    shortName: 'Annual Report',
    entityType: 'Both',
    description: 'A periodic filing required by most states to keep your business in good standing. Includes updated business information and a state fee.',
    longDescription: `Annual Reports (also called Annual Statements, Biennial Reports, or Periodic Reports depending on the state) are required filings to maintain your business in good standing with the state.

These reports update the state on your business information including address, registered agent, and officers/members. Missing an annual report can result in penalties, loss of good standing, or even administrative dissolution.`,
    whatIncluded: [
      'Current business address verification',
      'Registered agent information',
      'Names of officers, directors, or members',
      'Principal place of business',
      'Nature of business activities',
      'Authorized shares (for corporations)'
    ],
    whyNeeded: [
      'Maintains good standing with the state',
      'Avoids late fees and penalties',
      'Prevents administrative dissolution',
      'Keeps business information current',
      'Required for business licenses and contracts'
    ],
    timeline: '1-3 business days',
    price: 200,
    note: 'Due dates and fees vary by state',
    relatedFilings: ['registered-agent-appointment', 'name-amendment']
  },
  'registered-agent-appointment': {
    name: 'Registered Agent Appointment',
    shortName: 'Registered Agent',
    entityType: 'Both',
    description: 'Designate or change your registered agent - the person or company authorized to receive legal documents on behalf of your business.',
    longDescription: `A Registered Agent (also called Statutory Agent or Agent for Service of Process) is a person or company designated to receive legal documents, tax notices, and official government correspondence on behalf of your business.

Every LLC and corporation must have a registered agent with a physical address in the state of formation. You can serve as your own registered agent, but many businesses choose a professional service for privacy and reliability.`,
    whatIncluded: [
      'Physical street address in the state (no P.O. boxes)',
      'Available during normal business hours',
      'Accepts service of process (lawsuits)',
      'Receives state correspondence and tax notices',
      'Forwards documents to business owners',
      'Maintains compliance calendar'
    ],
    whyNeeded: [
      'Required by state law for all LLCs and corporations',
      'Ensures you receive important legal documents',
      'Provides privacy (your home address not public)',
      'Professional availability during business hours',
      'Never miss a deadline or legal notice'
    ],
    timeline: 'Same day to 3 business days',
    price: 150,
    note: 'Annual service fee',
    relatedFilings: ['articles-of-organization', 'annual-report']
  },
  'name-amendment': {
    name: 'Name Amendment',
    shortName: 'Name Change',
    entityType: 'Both',
    description: 'Officially change your business name with the state. Requires filing an amendment to your formation documents.',
    longDescription: `A Name Amendment (also called Articles of Amendment or Certificate of Amendment) officially changes your business name with the state. This is different from a DBA/trade name - it actually changes your legal entity name.

After filing, you'll need to update all business documents, bank accounts, licenses, and contracts to reflect the new name.`,
    whatIncluded: [
      'Current legal business name',
      'New proposed business name',
      'Name availability verification',
      'Amendment filing with state',
      'Updated formation certificate',
      'Guidance on updating other documents'
    ],
    whyNeeded: [
      'Rebrand your business officially',
      'Correct a misspelling in original filing',
      'Expand into new business areas',
      'Acquire or merge with another business',
      'Improve marketability of business name'
    ],
    timeline: '3-10 business days',
    price: 200,
    note: '+ state filing fee',
    relatedFilings: ['operating-agreement', 'annual-report']
  },
  'member-manager-change': {
    name: 'Member/Manager Change',
    shortName: 'Ownership Change',
    entityType: 'LLC',
    description: 'Update your LLC\'s ownership structure by adding, removing, or changing members or managers.',
    longDescription: `A Member or Manager Change filing updates the ownership or management structure of your LLC with the state. This may be required when members join or leave, ownership percentages change, or management roles are reassigned.

Some states require this to be filed as an amendment, while others only require updates in the annual report. Either way, your Operating Agreement should always reflect current ownership.`,
    whatIncluded: [
      'Current member/manager information',
      'New member/manager details',
      'Updated ownership percentages',
      'Amendment filing (if required by state)',
      'Updated Operating Agreement',
      'Transfer documentation'
    ],
    whyNeeded: [
      'Add new investors or partners',
      'Remove departing members',
      'Update ownership percentages',
      'Change management structure',
      'Document buy-sell transactions'
    ],
    timeline: '3-10 business days',
    price: 200,
    note: '+ state filing fee (if required)',
    relatedFilings: ['operating-agreement', 'annual-report', 'dissolution-filing']
  },
  'dissolution-filing': {
    name: 'Dissolution Filing',
    shortName: 'Dissolution',
    entityType: 'Both',
    description: 'Officially close your business with the state. Required to avoid ongoing fees and compliance requirements.',
    longDescription: `A Dissolution Filing (also called Articles of Dissolution or Certificate of Dissolution) officially terminates your business entity with the state. This is an important step when closing a business to avoid ongoing annual report fees, franchise taxes, and compliance requirements.

Simply stopping business operations doesn't close your entity - you must file dissolution documents with the state.`,
    whatIncluded: [
      'Articles/Certificate of Dissolution',
      'Final tax clearance (if required)',
      'Asset distribution documentation',
      'Final annual report (if required)',
      'Cancellation of business licenses',
      'Notification to creditors'
    ],
    whyNeeded: [
      'Legally terminates the business entity',
      'Stops ongoing state fees and taxes',
      'Releases you from compliance requirements',
      'Provides clear closure for legal purposes',
      'Protects you from future liabilities of the entity'
    ],
    timeline: '5-15 business days',
    price: 200,
    note: '+ state filing fee',
    relatedFilings: ['annual-report', 'member-manager-change']
  },
  'articles-of-incorporation': {
    name: 'Articles of Incorporation',
    shortName: 'Articles of Incorporation',
    entityType: 'C-Corp',
    description: 'The foundational document that officially creates your corporation with the state. This is the first filing for any new C-Corporation.',
    longDescription: `Articles of Incorporation (also called Certificate of Incorporation or Corporate Charter) is the legal document filed with the state to officially create your Corporation.

This document establishes your corporation as a legal entity and defines its basic structure including authorized shares, registered agent, and incorporators.`,
    whatIncluded: [
      'Corporation name and principal address',
      'Registered agent name and address',
      'Number and type of authorized shares',
      'Par value of shares (if any)',
      'Names of incorporators',
      'Purpose of the corporation',
      'Duration (usually perpetual)'
    ],
    whyNeeded: [
      'Legally creates your corporation',
      'Required to issue stock',
      'Establishes corporate structure',
      'Necessary for venture capital funding',
      'Provides personal liability protection'
    ],
    timeline: '3-10 business days',
    price: 299,
    relatedFilings: ['corporate-bylaws', 'ein-registration', 'stock-amendment']
  },
  'corporate-bylaws': {
    name: 'Corporate Bylaws',
    shortName: 'Bylaws',
    entityType: 'C-Corp',
    description: 'The internal rules governing how your corporation operates, including board meetings, officer roles, and shareholder rights.',
    longDescription: `Corporate Bylaws are the internal rules and procedures that govern how your corporation operates. While not filed with the state, bylaws are essential for proper corporate governance and maintaining your corporate liability protection.

Bylaws define the roles of directors and officers, meeting procedures, voting requirements, and other operational details.`,
    whatIncluded: [
      'Board of Directors procedures',
      'Officer roles and responsibilities',
      'Meeting requirements (annual, special)',
      'Voting procedures and quorum requirements',
      'Stock issuance procedures',
      'Amendment procedures',
      'Indemnification provisions'
    ],
    whyNeeded: [
      'Required for proper corporate governance',
      'Defines officer and director duties',
      'Establishes meeting and voting procedures',
      'Required by investors and banks',
      'Protects corporate liability shield'
    ],
    timeline: 'Same day (after incorporation)',
    price: 0,
    includedWith: 'C-Corp Formation',
    relatedFilings: ['articles-of-incorporation', 'stock-amendment', 'director-officer-change']
  },
  'stock-amendment': {
    name: 'Stock Amendment',
    shortName: 'Stock Amendment',
    entityType: 'C-Corp',
    description: 'Change your corporation\'s stock structure, including authorized shares, par value, or stock classes.',
    longDescription: `A Stock Amendment modifies your corporation's authorized capital structure. This may be needed to increase authorized shares for new investors, create new classes of stock (preferred shares), or change par value.

Stock amendments require filing with the state and often require board and shareholder approval.`,
    whatIncluded: [
      'Current stock structure',
      'Proposed changes to authorized shares',
      'New stock classes (if applicable)',
      'Updated par value (if changing)',
      'Board resolution',
      'Shareholder approval (if required)',
      'State filing'
    ],
    whyNeeded: [
      'Raise additional capital through new shares',
      'Create preferred stock for investors',
      'Implement stock option plans',
      'Restructure equity for acquisition',
      'Split or reverse split shares'
    ],
    timeline: '5-15 business days',
    price: 200,
    note: '+ state filing fee',
    relatedFilings: ['corporate-bylaws', 'director-officer-change']
  },
  'director-officer-change': {
    name: 'Director/Officer Change',
    shortName: 'Director/Officer Change',
    entityType: 'C-Corp',
    description: 'Update your corporation\'s leadership by adding, removing, or changing directors or officers.',
    longDescription: `A Director or Officer Change updates the leadership of your corporation. Directors are elected by shareholders and oversee major decisions. Officers (CEO, CFO, Secretary, etc.) are appointed by the board and handle day-to-day operations.

Some states require filing when directors change, while others only require updates in the annual report.`,
    whatIncluded: [
      'Current director/officer information',
      'New director/officer details',
      'Board resolutions',
      'State filing (if required)',
      'Updated corporate records',
      'Meeting minutes'
    ],
    whyNeeded: [
      'Appoint new directors or officers',
      'Remove departing leadership',
      'Comply with investor board seats',
      'Update after elections',
      'Maintain accurate corporate records'
    ],
    timeline: '3-10 business days',
    price: 200,
    note: '+ state filing fee (if required)',
    relatedFilings: ['corporate-bylaws', 'stock-amendment', 'annual-report']
  }
};

const POPULAR_STATES = [
  { slug: 'delaware', name: 'Delaware', code: 'DE' },
  { slug: 'wyoming', name: 'Wyoming', code: 'WY' },
  { slug: 'texas', name: 'Texas', code: 'TX' },
  { slug: 'florida', name: 'Florida', code: 'FL' },
  { slug: 'california', name: 'California', code: 'CA' },
  { slug: 'nevada', name: 'Nevada', code: 'NV' }
];

const USAFilingPage = () => {
  const { filing: filingSlug } = useParams();
  const filing = FILING_CONFIG[filingSlug];

  if (!filing) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-slate-200">
        <Header />
        <div className="flex items-center justify-center py-32" data-testid="filing-not-found">
          <SEOHead title="Filing Not Found" description="The requested filing type could not be found." siteName={SITE_NAME} noindex={true} />
          <div className="text-center px-4">
            <h1 className="font-serif text-3xl font-semibold text-white mb-4">Filing Not Found</h1>
            <p className="text-slate-400 mb-8">The filing type you're looking for doesn't exist.</p>
            <Link to="/usa/" className="btn-corporate inline-flex items-center gap-2">
              View All Services <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedFilings = (filing.relatedFilings || [])
    .map(slug => ({ ...FILING_CONFIG[slug], slug }))
    .filter(f => f.name);

  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": filing.name,
    "description": filing.description,
    "provider": { "@type": "Organization", "name": SITE_NAME, "url": BASE_URL }
  };

  return (
    <div className="min-h-screen bg-[#0b1310] text-slate-200" data-testid="usa-filing-page">
      <SEOHead
        title={`${filing.name} | ${filing.entityType} Filing Service`}
        description={filing.description}
        siteName={SITE_NAME}
        canonical={`${BASE_URL}/usa/filings/${filingSlug}/`}
        keywords={`${filing.name}, ${filing.entityType} filing, business filing, ${filing.shortName}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLdSchema)}</script>
      </Helmet>

      <Header />

      {/* Hero Section - Dark */}
      <section className="bg-[#0A0A0A]" data-testid="filing-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <nav className="mb-10" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-slate-400 flex-wrap">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li><Link to="/usa/" className="hover:text-white transition-colors">US Formation</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-emerald-400 font-medium">{filing.shortName}</li>
            </ol>
          </nav>
          
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 bg-neutral-800 text-white px-4 py-2 mb-6 text-sm font-medium">
                <FileText className="w-4 h-4 text-emerald-400" />
                {filing.entityType === 'Both' ? 'LLC & C-Corp' : filing.entityType}
              </div>
              
              <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tight leading-[1.1] mb-8">
                {filing.name}
              </h1>
              <p className="text-xl text-slate-400 leading-relaxed mb-8">
                {filing.description}
              </p>
              
              <div className="flex flex-wrap gap-8 text-sm text-slate-400 mb-10">
                {filing.price > 0 ? (
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-emerald-400" />
                    <span>From <span className="text-white font-medium">${filing.price}</span>{filing.note ? ` ${filing.note}` : ''}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-emerald-400" />
                    <span>Included with {filing.includedWith}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>{filing.timeline}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>Expert Support</span>
                </div>
              </div>
              
              <a href={APP_URL} className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-[#04110c] font-semibold hover:bg-emerald-400 transition-colors" data-testid="filing-hero-cta">
                Get Started <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
            
            {/* What's Included Card */}
            <div className="bg-neutral-900 border border-neutral-800 p-10" data-testid="filing-included-card">
              <h2 className="text-xl font-semibold text-white mb-8">What's Included</h2>
              <ul className="space-y-4">
                {filing.whatIncluded.slice(0, 7).map((item, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Long Description */}
      <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="filing-description">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          {filing.longDescription.split('\n\n').map((paragraph, index) => (
            <p key={index} className="text-xl text-slate-400 leading-relaxed mb-6 last:mb-0">
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      {/* Why You Need This */}
      <section className="py-24 md:py-32 bg-[#111c17]" data-testid="filing-why-needed">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl mb-16">
            <p className="text-emerald-400 font-semibold text-sm uppercase tracking-widest mb-4">Benefits</p>
            <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight">
              Why You Need {filing.shortName}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filing.whyNeeded.map((reason, index) => (
              <div 
                key={index}
                className="bg-[#070d0b] border border-white/10 p-8 flex items-start gap-4"
              >
                <div className="w-10 h-10 bg-[#111c17] flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <p className="text-slate-400">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Available States */}
      <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="filing-states">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Jurisdictions</p>
            <h2 className="font-serif text-4xl font-semibold text-white tracking-tight mb-4">
              Available in All 50 States
            </h2>
            <p className="text-xl text-slate-400">
              We can help with {filing.shortName} in any US state. Popular choices:
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {POPULAR_STATES.map((state) => (
              <Link
                key={state.slug}
                to={`/usa/states/${state.slug}/`}
                className="group bg-[#111c17] border border-white/10 p-5 text-center hover:shadow-card-hover transition-all"
              >
                <span className="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
                  {state.code}
                </span>
                <p className="text-sm text-slate-400">{state.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related Filings */}
      {relatedFilings.length > 0 && (
        <section className="py-24 md:py-32 bg-[#111c17]" data-testid="filing-related">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Related Services</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight">
                Related Filings
              </h2>
              <p className="text-xl text-slate-400 mt-4">
                Other filings you might need
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedFilings.map((related) => (
                <Link
                  key={related.slug}
                  to={`/usa/filings/${related.slug}/`}
                  className="group bg-[#070d0b] border border-white/10 p-8 hover:shadow-card-hover transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#111c17] flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
                        {related.name}
                      </h3>
                      <p className="text-slate-400 text-sm mt-1">{related.entityType}</p>
                      <p className="text-slate-400 text-sm mt-2 line-clamp-2">{related.description}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing */}
      {filing.price > 0 && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="filing-pricing">
          <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="bg-[#111c17] border border-white/10 overflow-hidden">
              <div className="bg-[#111c17] px-8 py-6">
                <h3 className="text-white font-semibold text-xl">{filing.name}</h3>
                <p className="text-slate-500 text-sm">{filing.entityType === 'Both' ? 'LLC & C-Corp' : filing.entityType}</p>
              </div>

              <div className="px-8 py-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <span className="font-medium text-white text-lg">{filing.shortName}</span>
                    <p className="text-sm text-slate-400">{filing.timeline}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-serif text-white">${filing.price}</span>
                    {filing.note && <p className="text-xs text-slate-400">{filing.note}</p>}
                  </div>
                </div>
              </div>

              <div className="px-8 py-6 bg-[#070d0b] border-t border-white/10">
                <a
                  href={APP_URL}
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-[#0A0A0A] text-white font-semibold hover:bg-neutral-800 transition-colors"
                >
                  Get {filing.shortName}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#0A0A0A]" data-testid="filing-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight mb-6">
            Need Help with {filing.shortName}?
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Our experts can guide you through the process. {filing.timeline} timeline. Expert support included.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={APP_URL}
              className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-[#04110c] font-semibold hover:bg-emerald-400 transition-colors"
            >
              Get Started {filing.price > 0 ? `- $${filing.price}` : ''}
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <a
              href="mailto:hi@starteaseai.com"
              className="inline-flex items-center justify-center px-8 py-4 border border-neutral-700 text-white font-medium hover:border-emerald-500 transition-colors"
            >
              Talk to an Expert
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default USAFilingPage;
